/**
 * Microsoft 365 guide anchors (plan
 * guide-can-walk-a-person-through-a-task-highlight, item guide-anchors): the
 * setup wizard's controls Guide may point at, and how the console observes
 * each one done. Kernel: shared/guideAnchor.ts; registry and catalogue:
 * shared/guideAnchorRegistry.ts. The page marks each control with
 * guideAnchor(name) (frontend/src/platform/guide/guideAnchor.js).
 *
 * Help lines are the texts the page shows next to each control (hint, note or
 * row summary); the page renders them from here (guideAnchorHelp), so the
 * page and Guide never disagree.
 *
 * Completion is the honest signal: `route` for moving between steps and
 * closing, `state` for what the console reads back from its existing reads
 * (the Email read, the areas read), `click` for plain actions nothing reads
 * back (Copy, Validate again of the areas: its answer is this page's own
 * state, not a server read).
 */

import { defineGuideAnchors, defineGuideStateReads, type GuidePage } from '../guideAnchor'
import { contracts } from './msgraph.contracts'
import { identity } from './msgraph'

/** The facts of the Email read (GET /api/msgraph/email) a completion tests. */
interface EmailViewFacts {
  mailbox?: string | null
  writeTo?: readonly string[]
  actOnFrom?: readonly string[]
  identity?: { kind?: string; inTenant?: boolean } | null
  status?: { state?: string } | null
}

/** The facts of the areas read (GET /api/msgraph/agent-areas/details) a completion tests. */
interface AgentAreasFacts {
  areas?: ReadonlyArray<{ key: string; approved?: boolean }>
}

/**
 * The Email read's facts, shared with the Email step's rows
 * (frontend/src/features/msgraph/EmailPanel.jsx emailRows) so a row and a
 * guide step can never disagree about done.
 */
export const emailFacts = {
  mailboxSet: (view: EmailViewFacts | null | undefined) => Boolean(view?.mailbox),
  mailAppAdded: (view: EmailViewFacts | null | undefined) => view?.identity?.kind === 'mail-app' && view.identity.inTenant === true,
  listsSet: (view: EmailViewFacts | null | undefined) => Boolean(view?.writeTo?.length || view?.actOnFrom?.length),
  working: (view: EmailViewFacts | null | undefined) => view?.status?.state === 'ok',
}

export const guideStateReads = defineGuideStateReads(identity, contracts, {
  'msgraph.email.mailbox-set': { label: 'A shared mailbox is saved', contract: 'getEmailChannel', done: emailFacts.mailboxSet },
  'msgraph.email.mail-app-added': { label: 'The mail app is approved', contract: 'getEmailChannel', done: emailFacts.mailAppAdded },
  'msgraph.email.lists-set': { label: 'Allowed recipients or senders are configured', contract: 'getEmailChannel', done: emailFacts.listsSet },
  'msgraph.email.working': { label: 'Verification found the mailbox working', contract: 'getEmailChannel', done: emailFacts.working },
  'msgraph.areas.approved': {
    label: 'The area is approved',
    contract: 'getAgentAreaDetails',
    keyed: true,
    done: (view: AgentAreasFacts | null | undefined, key?: string) => (view?.areas ?? [])
      .some((area) => area.approved === true && (key === undefined || area.key === key)),
  },
})

const PAGE = '/microsoft-365'
/** Anywhere in the setup wizard: the step rail and Close setup are on every step. */
const SETUP: GuidePage = { path: PAGE, params: { setup: true } }
const step = (key: string): GuidePage => ({ path: PAGE, params: { setup: key } })
const toStep = (key: string) => ({ kind: 'route', path: PAGE, params: { setup: key } }) as const

export const guideAnchors = defineGuideAnchors(identity, guideStateReads, {
  // The step rail (below 768px it lives in the "All steps" sheet).
  'msgraph.setup.step.blueprints': { label: 'Blueprints step', page: SETUP, completion: toStep('model') },
  'msgraph.setup.step.areas': { label: 'Areas step', page: SETUP, completion: toStep('areas') },
  'msgraph.setup.step.validation': { label: 'Validation step', help: 'Each approved area is checked live, signed in as you, with one harmless read.', page: SETUP, completion: toStep('validation') },
  'msgraph.setup.step.email': { label: 'Email step', help: 'Routines, escalations and “check the mailbox for …” read and send as this one mailbox. Exchange grants it; you can take it back there.', page: SETUP, completion: toStep('email') },
  'msgraph.setup.step.summary': { label: 'Summary step', page: SETUP, completion: toStep('summary') },
  'msgraph.setup.all-steps': { label: 'All steps (phones)', page: SETUP, completion: { kind: 'click' } },
  'msgraph.setup.close': { label: 'Close setup', page: SETUP, completion: { kind: 'route', path: PAGE, params: { setup: null } } },

  // Areas: one Approve per area, keyed by the area's key (shared blueprints, Settings.Write).
  'msgraph.setup.areas.approve': { label: 'Approve area', help: 'Approve each area once for your whole organization: Microsoft shows two consent screens and brings you back here.', page: step('areas'), keyed: true, completion: { kind: 'state', read: 'msgraph.areas.approved' } },

  // Validation.
  'msgraph.setup.validation.validate-all': { label: 'Validate all (Validate again once checked)', page: step('validation'), completion: { kind: 'click' } },

  // Email.
  'msgraph.email.mailbox': { label: 'Shared mailbox address', help: 'Created in the Microsoft 365 admin center.', page: step('email'), completion: { kind: 'state', read: 'msgraph.email.mailbox-set' } },
  'msgraph.email.save-mailbox': { label: 'Save mailbox', page: step('email'), completion: { kind: 'state', read: 'msgraph.email.mailbox-set' } },
  'msgraph.email.add-mail-app': { label: 'Approve mail app', help: 'The app requests no permissions; mailbox access is granted in the next step.', page: step('email'), completion: { kind: 'state', read: 'msgraph.email.mail-app-added' } },
  'msgraph.email.copy-exchange-commands': { label: 'Copy the Exchange command', help: 'An Exchange administrator runs it in Azure Cloud Shell.', page: step('email'), completion: { kind: 'click' } },
  'msgraph.email.write-to': { label: 'Allowed recipients', help: 'Email to any other address is blocked.', page: step('email'), completion: { kind: 'state', read: 'msgraph.email.lists-set' } },
  'msgraph.email.act-on-from': { label: 'Allowed senders', help: 'Email from other senders is ignored and logged.', page: step('email'), completion: { kind: 'state', read: 'msgraph.email.lists-set' } },
  'msgraph.email.save-lists': { label: 'Save', page: step('email'), completion: { kind: 'state', read: 'msgraph.email.lists-set' } },
  'msgraph.email.validate': { label: 'Verify', help: 'Confirms the AI can access the mailbox.', page: step('email'), completion: { kind: 'state', read: 'msgraph.email.working' } },
})
